<template>
  <div
    class="owal-search-bar__container"
    :class="`light`"
    id="owal-search-bar-container"
  >
    <div class="owal-search-bar__search-controls">
      <div class="owal-search-bar__leading-controls">
        <slot name="leading-controls" />
      </div>
      <div class="owal-search-bar__search-input-with-accessories">
        <div class="owal-search-bar__leading-accessory">
          <slot name="leading-accessory">
            <font-awesome-icon
              class="owal-search-bar__icon_static"
              icon="search"
              style="color: #8492A6;"
            />
          </slot>
        </div>
        <input
          ref="input"
          v-model.trim="searchTerm"
          class="owal-search-bar__search-box"
          :placeholder="placeholder"
          @focus="editing=true"
          @blur="editing=false"
        />
        <font-awesome-icon
          class="clear-button"
          :class="editing && showClear && !!searchTerm ? `editing` : undefined"
          icon="times-circle"
          @click="clearSearchTerm"
        />
        <div
          v-if="this.hasFiltersOrSorts && this.canToggleFilters"
          class="owal-search-bar__trailing-accessory"
          @click="collapsed=!collapsed"
        >
          <slot
            name="trailing-accessory"
            >
            <template>
            <div
              class="owal-search-bar__trailing-accessory-circle"
              :class="{active: !collapsed}"
            >
              <font-awesome-icon icon="bars" />
            </div>
            </template>
          </slot>
        </div>
      </div>
      <div class="owal-search-bar__trailing-controls">
        <slot name="trailing-controls" />
      </div>
    </div>
    <owal-transition-slide
      from="top"
      axis="vertical"
    >
      <div
        v-show="!collapsed"
        slot="content"
        >
        <div
          v-show="this.filters.length > 0"
          class="owal-search-bar__filters"
        >
          <owal-tab-bar
            :titles="this.filterNames"
            :multiple="false"
            @change="this.updateFilters"
          />
        </div>
        <div
          v-if="tableHeader && visibleSorts.length > 1"
          class="owal-search-table-header-row__container"
          >
          <div
            v-for="header in this.visibleSorts"
            :key="header.key"
            class="header"
            :class="activeSort === header ? `bold` : ``"
            @click="activateSort(header)"
            >
            {{ header.displayName }}
            <font-awesome-icon
              v-if="lastSort === header && activeSort"
              icon="sort-up"
              />
            <font-awesome-icon
              v-else
              icon="sort-down"
              />
          </div>
        </div>
        <div
          v-else-if="visibleSorts.length > 1"
          class="owal-search-bar__sort"
          >
          <owal-button-multistate
            v-for="(sort, index) in this.visibleSorts"
            :key="sort.displayName"
            :class="buttonClassForSort(sort)"
            :states="statesForSort(sort)"
            :corners="radiusForIndex(index)"
            @click="activateSort(sort)"
            />
        </div>
      </div>
    </owal-transition-slide>
  </div>
</template>

<script>
/**
 * OwalSearchBar.vue
 *
 * @description A component which handles searching through a collection of data.
 *
 * @prop {type}
 *
 */

import { OwalSearchController } from './OwalSearchController'
import { OwalSearchPredicate } from './OwalSearchPredicate'
import { OwalTabBar } from '../tabs/'
import { OwalButtonMultistate } from '../buttons/'
import * as OwalTransitions from '../transitions/'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'OwalSearchBar',
  props: {
    /**
     * A placeholder string for the search input.
     */
    placeholder: {
      type: String,
      default: 'Search',
    },
    /**
     * An array of unfiltered and unsorted items.
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Properties of the items to search.
     */
    searchKeys: {
      type: Array,
      default: () => ['title'],
    },
    /**
     * @prop {Array<OwalFilterPredicate>} sorts A list of `OwalFilterPredicate` objects.
     */
    filters: {
      type: Array,
      default: () => [],
    },
    /**
     * @prop {Array<OwalSortPredicate>} sorts A list of `OwalSortPredicate` objects.
     */
    sorts: {
      type: Array,
      default: () => [], // TODO: Provide default search function.
    },
    /**
     * @prop {Boolean} tableHeader Determines if the sorts are presented as headers.
     */
    tableHeader: {
      type: Boolean,
      default: false,
    },
    /**
     * @prop {Function} callback A callback function which receives filtered results.
     */
    callback: {
      type: Function,
      default: undefined,
    },
    /**
     * @prop {Boolean} initiallyCollapsed Determines the initial state of the search bar.
     */
    initiallyCollapsed: {
      type: Boolean,
      default: false,
    },
    /**
     * @prop {Boolean} canToggleFilters Determines whether we support collapsing the filters.
     */
    canToggleFilters: {
      type: Boolean,
      default: true,
    },
    /**
     * @prop {Array<OwalSearchSynonyms>} synonyms An array of OwalSearchSynonym objects.
     */
    synonyms: {
      type: Array,
      default: () => [],
    },
    /** Controls if we can show/hide the clear button. */
    showClear: {
      type: Boolean,
      default: true,
    },
    /**
     * @prop {String} querySearchTerm Optional string to pre-fill search bar.
     */
    querySearchTerm: {
      type: String,
      default: undefined,
    },
  },
  data () {
    return {
      searchTerm: this.querySearchTerm,
      activeFilterIndices: [0],
      activeSort: undefined,
      reverse: false,
      lastSort: undefined,
      collapsed: this.initiallyCollapsed,
      editing: false,
    }
  },
  computed: {
    searchPredicate () {
      return new OwalSearchPredicate({ searchKeys: this.searchKeys })
    },
    filterNames () {
      const names = this.filters.map(filter => filter.displayName)
      return names
    },
    activeFilters () {
      const active = this.filters.filter((filter, index) => this.activeFilterIndices.includes(index))
      // console.debug(`Active: `, active)
      return active
    },
    filteredItems () {
      const searchTerm = this.searchTerm
      const sort = this.activeSort

      const searchController = new OwalSearchController()
      let searchPredicate = this.searchPredicate
      let filteredItems = Object.assign([], this.items)

      /** Filter first. */
      for (const index in this.activeFilters) {
        const filter = this.activeFilters[index]
        filteredItems = searchController.searchUsingPredicate(filter.value, filter, filteredItems)
      }

      /** Handle search. */
      if (searchTerm) {
      //    console.debug(`=> Applying search term: `, searchTerm)
        filteredItems = searchController.searchUsingPredicate(searchTerm, searchPredicate, filteredItems, this.synonyms)
      //    console.debug(`-> # items after search: `, filteredItems.length)
      }

      /** Handle sort. */
      if (sort) {
        const sortIndex = this.sorts.findIndex(incomingSort => incomingSort.displayName === sort.displayName)
        if (sortIndex > -1) {
          searchController.sortUsingPredicate(sort, filteredItems)
          if (this.reverse) {
          //    console.debug(`==> Reversing and canceling active sort.`)
            filteredItems.reverse()
            // this.activeSort = undefined // eslint-disable-line
          }
        }
      //    console.debug(`-> Sorting...`)
      }
      //    console.debug(`-> Done! # Items: `, filteredItems.length)

      return filteredItems
    },
    hasFiltersOrSorts () {
      const hasSorts = this.visibleSorts.length > 0
      const hasFilters = this.filters.length > 0

      return hasSorts || hasFilters
    },
    visibleSorts () {
      return this.sorts.filter(sort => !sort.hidden)
    },
  },
  watch: {
    filteredItems (newValue, oldValue) {
      const callback = this.callback
      if (callback) {
        callback(newValue, this.searchTerm, this.filters, this.sorts)
      } else {
      //    console.debug('Please supply a callback to receive sorted and filtered output.')
      }
      // console.debug('Old: ', oldValue.length, ` New: `, newValue.length)
    },
  },
  mounted () {
    if (this.visibleSorts.length === 1) {
      this.activateSort(this.visibleSorts[0])
    }
    const activeDefault = this.visibleSorts.filter(sort => sort.activeDefault)
    if (activeDefault.length >= 1) {
      this.activateSort(activeDefault[0])
    }
  },
  methods: {
    updateFilters (indices) {
      // console.debug(`Change`, indices)
      this.activeFilterIndices = indices
    },
    clearSearchTerm () {
      this.$nextTick(() => {
        this.searchTerm = ''
      })
    },
    statesForSort (sort) {
      const states = [{
        title: sort.displayName,
        identifier: sort.displayName.toLowerCase().replace(' ', '-'),
        icon: 'sort-up',
      },
      {
        title: sort.displayName,
        identifier: sort.displayName.toLowerCase().replace(' ', '-'),
        icon: 'sort-down',
      }]
      return states
    },
    activateSort (sort) {
      this.lastSort = this.activeSort
      this.reverse = (this.activeSort === sort) ? !this.reverse : false
      this.activeSort = sort
    },
    radiusForIndex (index) {
      const leftRadii = {
        topLeft: '0.35rem',
        topRight: 0,
        bottomLeft: '0.35rem',
        bottomRight: 0,
      }
      const middleRadii = {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0,
      }
      const rightRadii = {
        topLeft: 0,
        topRight: '0.35rem',
        bottomRight: '0.35rem',
        bottomLeft: 0,
      }
      const standaloneRadii = {
        topLeft: '0.35rem',
        topRight: '0.35rem',
        bottomRight: '0.35rem',
        bottomLeft: '0.35rem',
      }

      if (this.sorts.length === 1) {
        return standaloneRadii
      }

      if (index === 0) {
        return leftRadii
      } else if (index === this.sorts.length - 1) {
        return rightRadii
      } else {
        return middleRadii
      }
    },
    buttonClassForSort (sort) {
      return this.isActiveSort(sort) ? 'owal-search-bar__active-sort' : ''
    },
    isActiveSort (sort) {
      if (!sort || !this.activeSort) {
        return false
      }
      return this.activeSort === sort
    },
  },
  components: {
    OwalTabBar,
    OwalButtonMultistate,
    ...OwalTransitions,
    FontAwesomeIcon,
  },
}
</script>

<style scoped>
/** The entire search bar. */
.owal-search-bar__container {
  min-height: 3.1459rem;
  max-height: 9.4377rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: 0.5rem;
  overflow: hidden;
}

/** A dark background for the search bar. */
.dark {
  background: #324057;
}

/** A light background for the search bar. */
.light {
  background: #eff2f7;
}

/** A container around search input and accessory buttons. */
.owal-search-bar__search-controls {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
  width: 100%;
  z-index: 99;
}

.owal-search-bar__leading-controls,
.owal-search-bar__trailing-controls {
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  border: none;
  padding: 0;
  margin: 0;
  /* margin: 0.5rem 0; */
  height: 2.1428571429rem;
}

.owal-search-bar__leading-accessory,
.owal-search-bar__trailing-accessory {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 2;
  width: fit-content;
  height: 2.1428571429rem;
  padding: 0;
  margin: 0;
}

.owal-search-bar__search-input-with-accessories {
  height: 2.1428571429rem;
  flex-direction: row;
  display: flex;
  border: none;
  border-radius: 0.35rem;
  margin: 0.5rem;
  padding: 0.5rem;
  flex-grow: 99;
  flex-shrink: 0;
  height: 2.1428571429rem;
  flex-direction: row;
  background: #e5e9f2;
  box-sizing: border-box;
  pointer-events: auto;
  color: #324057;
  flex-direction: row;
  align-content: center;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(50, 64, 87, 0.15);
}

/** The input field. */
.owal-search-bar__search-box {
  display: flex;
  border: none;
  border-top: 1px solid rgba(50, 64, 87, 0.15);
  border-bottom: 1px solid rgba(50, 64, 87, 0.15);
  padding: 0.5rem 0.5rem 0.5rem;
  margin: 0.5rem 0;
  flex-grow: 99;
  flex-shrink: 0;
  height: 2.1428571429rem;
  flex-direction: row;
  background: #e5e9f2;
  box-sizing: border-box;
  pointer-events: auto;
  color: #324057;
  outline: none;
}

.owal-search-bar__filters {
  display: flex;
  flex-direction: row;
  /* background: blue; */
}

.owal-search-bar__sort {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
  margin: 0.35rem;
  padding: 0.35rem;
  box-sizing: border-box;
  flex-grow: 0;
  /* background: red; */
}

.owal-search-bar__active-sort {
  background: #8492a6 !important;
  color: #eff2f7 !important;
  user-select: none;
}

/** The filter toggle accessory's size, layout, etc. */
.owal-search-bar__trailing-accessory-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem;
  height: 1.75rem;
  width: 1.75rem;
  border-radius: 50%;
  transform: scale(0.8);
  cursor: pointer;
  transition: all 300ms ease;
}

/** Appearance: Hover state when inactive, and regular state when filters are active. */
.owal-search-bar__trailing-accessory-circle:hover {
  border: 1px solid #324057;
  color: #324057;
}

/** Appearance: Hover state when active, and regular state when filters are active. */
.owal-search-bar__trailing-accessory-circle:hover,
.active:hover {
  border: 1px solid #324057;
  opacity: 1;
}

.active:hover {
  color: #e5e9f2;
}

.active {
  opacity: 0.65;
  transition: opacity 300ms linear;
  background: #324057;
  color: #e5e9f2;
}

.clear-button {
  opacity: 0.01;
  transition: opacity 200ms linear;
}

.clear-button.editing {
  cursor: pointer;
  opacity: 0.5;
}

.clear-button.editing:hover {
  opacity: 1;
}

/** Header for "table attached" mode. */
.owal-search-table-header-row__container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(1px, 1fr));
  grid-template-rows: repeat(1fr 1fr 1fr);
  flex-direction: row;
  justify-content: stretch;
  width: 100%;
  padding: 0.35rem 0;
  border-top: 1px solid #eff2f7;
  border-bottom: 1px solid #324057;
  background: #fff;
}

.owal-search-table-header-row__container .header {
  flex-grow: 1;
  flex-shrink: 1;
  text-align: center;
  font-weight: 700;
  cursor: pointer;
}

.header::selection {
  background: none;
}

.owal-search-table-header-row__container .header:not(:first-of-type) {
  border-left: 1px solid #32405733;
}

.owal-search-table-header-row__container .bold {
  font-weight: 900;
}
</style>
